// Reassembles the situation from the deltas the server broadcasts for its
// append-only lists (game log, completed trades, rejected trades). The server
// only sends the entries added since that room's last broadcast, so the client
// holds the full lists and appends to them.
//
// Every delta carries `total`, the true length of the server's list. That is
// what makes this safe: the client can tell "these are exactly the entries I am
// missing" from "there is a hole between what I have and what just arrived",
// and ask for a full resync in the second case rather than rendering a log with
// entries silently missing from the middle.

// Applies one delta to the copy of a list the client already holds. Returns
// null when it cannot be applied without leaving a hole — the caller resyncs.
export const applyAppendDelta = <T>(
  current: T[] | undefined,
  delta: IAppendDelta<T> | undefined
): T[] | null => {
  const have = current || []
  // No delta for this list means the server sent nothing about it (an older
  // server, or a payload shape without appends) — leave what we have.
  if (!delta) return have
  // A reset: the list starts over, so `items` is the whole new list.
  if (delta.full) return delta.items
  // Index of this delta's first entry within the full list.
  const start = delta.total - delta.items.length
  // Already at or past the server's length: nothing new to take. Happens right
  // after a full snapshot, when the room's counter is still behind us.
  if (have.length >= delta.total) return have
  // Missed entries between what we hold and where this delta starts.
  if (have.length < start) return null
  // Overlap (have.length > start) is normal after a snapshot; skip the part we
  // already hold and append only the remainder.
  return [...have, ...delta.items.slice(have.length - start)]
}

// Rebuilds a full situation from the wire situation plus its deltas. Returns
// null if any list has a gap, which means the caller should request a resync
// and drop this update.
export const mergeSituationUpdate = (
  previous: IGameSituation | undefined,
  wire: IWireSituation,
  appends: ISituationAppends | undefined
): IGameSituation | null => {
  const historyItems = applyAppendDelta(
    previous?.historyItems,
    appends?.historyItems
  )
  const completedTrades = applyAppendDelta(
    previous?.completedTrades,
    appends?.completedTrades
  )
  const rejectedTrades = applyAppendDelta(
    previous?.rejectedTrades,
    appends?.rejectedTrades
  )
  if (!historyItems || !completedTrades || !rejectedTrades) return null
  return {
    ...(wire as IGameSituation),
    historyItems,
    completedTrades,
    rejectedTrades,
  }
}
